"use client"
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../Contexts/AuthContext'
import { useEffect, useState } from 'react'
import TokenChecker from './TokenChecker'
import { error as logError } from "../utils/logger";


export default function TaskAcceptance() {
  
const [searchParams] = useSearchParams();
  const uidb64 = searchParams.get("uidb64");
  const token = searchParams.get("token");
  const taskId = searchParams.get("taskId");
  const action = searchParams.get("action");
  const [status, setStatus] = useState("loading");
  const [taskDetails, setTaskDetails] = useState(null);
  const { authData } = useAuth();
  const navigate = useNavigate();

 useEffect(() => {
  if (!authData || !authData.accessToken) return;

  const validateLink = async () => {
    
    try {
      const res = await TokenChecker(`/activate/${uidb64}/${token}/${taskId}/${action}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          }
        }
      );
      const data = await res.json();

      if (res.status === 403) {
        const intended = window.location.pathname + window.location.search;
        const encoded = encodeURIComponent(intended);
        navigate(`/login?next=${encoded}`);
        return;
      }

      if (res.status !== 200 || data.status === "invalid") {
        setStatus("error");
      } else {
        setTaskDetails(data.task);
        setStatus("valid");
      }
    } catch (err) {
      setStatus("error");
    }
  };

  validateLink();
}, [uidb64, token, taskId, action, authData ,navigate]);




    const handleAccept = async (trusted = false) => {
    try {
      const res = await TokenChecker("/accept_task", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          task_id: taskId,
          user_email: authData.email,
          trusted: trusted,
          role: action === "invite_" ? "invite" : "assign"
        })
      });

      if (res.ok) {
        if (window.opener && !window.opener.closed) {
              window.opener.postMessage({
              type: 'TASK_ACCEPTED',
              taskId: taskId
        }, window.location.origin);

        window.close();  // closes the email-triggered tab
      } else {
        navigate('/home');  // fallback for manual access
      }

      }
    } catch (err) {
      logError("Failed to accept task:", err);
    }
  };




  if (status === "loading") return <p className="text-center mt-10">Loading task details...</p>;
if (status === "error") return <p className="text-center mt-10 text-red-500">Invalid or expired link.</p>;

const priorityMap = {
  0: { label: "Immediate", textColor: "text-red-600", bgColor: "bg-red-50", borderColor: "border-red-200" },
  1: { label: "Moderate", textColor: "text-yellow-600", bgColor: "bg-yellow-50", borderColor: "border-yellow-200" },
  2: { label: "Lenient", textColor: "text-green-600", bgColor: "bg-green-50", borderColor: "border-green-200" }
};

const priorityInfo = priorityMap[taskDetails?.priority] || { label: "Unknown", textColor: "text-gray-600", bgColor: "bg-gray-50", borderColor: "border-gray-200" };


return (
  <div className="min-h-screen bg-gray-50">
    {/* Header */}
    <div className="border-b bg-white">
      <div className="flex items-center px-6 py-4">
        <h1 className="text-3xl font-bold" style={{ color: "#4299E1" }}>
          FLOWMAN
        </h1>
      </div>
    </div>

    {/* Main Content */}
    <div className="flex items-center justify-center min-h-[calc(100vh-80px)] p-6">
      <div className="w-full max-w-2xl space-y-6">
        {/* Task Invitation Card */}
        <div className="bg-white rounded-lg shadow-lg border-0 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-800">
                {action === "invite_" ? "You've been invited to a task!" : "You've been assigned a task!"}
              </h2>
              <span className={`px-3 py-1 text-sm font-medium ${priorityInfo.textColor} ${priorityInfo.bgColor} border ${priorityInfo.borderColor} rounded-full`}>
                {priorityInfo.label}
              </span>

            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Task Details */}
            <div className="space-y-4 text-center">
              <h3 className="text-lg font-medium text-gray-900 block">{taskDetails.title}</h3>

              <p className="text-gray-600 leading-relaxed">
                {taskDetails.description || "No description provided."}
              </p>

              {/* Task Meta Information */}
              <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>From: {taskDetails.owner}</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>Due: {taskDetails.deadline || "No due date"}</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <button
                className="flex-1 font-medium py-3 px-6 rounded-lg text-white transition-colors duration-200 hover:opacity-90"
                style={{ backgroundColor: "#4299E1" }}
                onClick={() => handleAccept(false)}
              >
                Accept the {action === "invite_" ? "Invite" : "Assignment"}
              </button>

              <button
                className="flex-1 font-medium py-3 px-6 rounded-lg transition-colors duration-200 hover:bg-blue-50"
                style={{
                  border: "1px solid #4299E1",
                  color: "#4299E1",
                  backgroundColor: "transparent",
                }}
                onClick={() => handleAccept(true)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#EBF8FF";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                Add as a trusted User
              </button>
            </div>

            {/* Trusted User Description */}
            <div
              className="rounded-lg p-4 mt-4"
              style={{ backgroundColor: "#EBF8FF", border: "1px solid #BEE3F8" }}
            >
              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 mt-0.5 flex-shrink-0"
                  style={{ color: "#4299E1" }}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="space-y-1">
                  <h4 className="font-medium" style={{ color: "#2B6CB0" }}>
                    What does "Add as trusted User" mean?
                  </h4>
                  <p className="text-sm leading-relaxed" style={{ color: "#2C5282" }}>
                    This user will be added as trusted user, so that you don't receive an email everytime they send
                    you an invite or assign — you’ll see it instantly in Flowman.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);
}
